import type { Passkey } from 'models';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Col, InfinityLoading, Row, Text, Title, message } from 'ui';
import { getBrowserName, getOsName } from 'common/utils';
import { useCreatePasskey, usePasskeysInfiniteQuery } from '../queries';
import { useDeletePasskeyConfirmModal, usePasskeyRegistrationError } from './hooks';
import { PasskeyItem } from './passkey-item';
import { PasskeySkeleton } from './passkey-item-skeleton';

const Passkeys = () => {
  const { t } = useTranslation();
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = usePasskeysInfiniteQuery({
    pagination: { pageSize: 10 },
    sorter: { updatedAt: 'descend' },
  });
  const { isPending: isCreatingPasskey, mutate: createPasskey } = useCreatePasskey();
  const { confirm: confirmDelete, contextHolder } = useDeletePasskeyConfirmModal();

  const passkeys = useMemo(() => data?.pages.flatMap((page) => page.data), [data]);

  const handleDelete = useCallback(
    (passkey: Passkey) => {
      confirmDelete({ passkeyId: passkey._id, passkeyName: passkey.name });
    },
    [confirmDelete],
  );

  const handlePasskeysCreationError = usePasskeyRegistrationError();

  const handleCreate = useCallback(() => {
    const name = `${getOsName()} (${getBrowserName()}) | ${import.meta.env.VITE_APP_TITLE}`;
    createPasskey(
      { name },
      {
        onError(error) {
          handlePasskeysCreationError(error);
        },
        onSuccess() {
          message.success(t('Passkey created successfully.'));
        },
      },
    );
  }, [createPasskey, handlePasskeysCreationError, t]);

  return (
    <Row gutter={[0, 'ms']}>
      {contextHolder}
      <Col span={24}>
        <Title level={3}>Passkeys</Title>
      </Col>
      <Col span={24}>
        <Text>
          With passkeys on BigHeart, you can securely sign in using your fingerprint, face, screen lock, or security
          key—no password needed. Passkeys and security keys can also serve as an extra verification step when logging
          in with a password. Keep your screen locks private and security keys safe so only you can access your BigHeart
          account
        </Text>
      </Col>
      <Col span={24}>
        {isLoading ? (
          <PasskeySkeleton />
        ) : (
          passkeys?.map((passkey) => <PasskeyItem key={passkey.id} onDelete={handleDelete} passkey={passkey} />)
        )}
      </Col>
      <Col span={24}>
        <InfinityLoading hasMoreData={hasNextPage} isLoading={isFetchingNextPage} onEnter={fetchNextPage} />
      </Col>
      <Col span={24}>
        <Button loading={isCreatingPasskey} onClick={handleCreate} type="primary">
          Create a passkey
        </Button>
      </Col>
    </Row>
  );
};

export default Passkeys;
